import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  AiKnowledgeBaseAiMode,
  AiKnowledgeBaseEntryDto,
  AiKnowledgeBaseSourceType,
  CreateAiKnowledgeBaseEntryRequestDto,
  UpdateAiKnowledgeBaseEntryRequestDto,
} from '@/shared/api/ai-knowledge-base';

const AI_MODES: AiKnowledgeBaseAiMode[] = ['suggest', 'assist', 'autopilot'];
const DEFAULT_AI_MODES: AiKnowledgeBaseAiMode[] = ['suggest'];

function parseCommaList(value: string, limit: number) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, limit);
}

function parseLines(value: string, limit: number) {
  return value
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, limit);
}

function formatCommaList(values: string[]) {
  return values.join(', ');
}

function formatLines(values: string[]) {
  return values.join('\n');
}

function isoToDateInput(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function dateInputToIso(value: string) {
  if (!value) return null;
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function normalizeAiModes(
  values: AiKnowledgeBaseAiMode[]
): AiKnowledgeBaseAiMode[] {
  return values.length > 0 ? values : DEFAULT_AI_MODES;
}

function getInitialAiModes(entry: AiKnowledgeBaseEntryDto | null) {
  return normalizeAiModes(entry?.applicability.ai_modes ?? DEFAULT_AI_MODES);
}

export function KnowledgeBaseArticleEditor({
  entry,
  disabled,
  isSaving,
  isDeleting,
  onCreate,
  onSave,
  onDelete,
}: {
  entry: AiKnowledgeBaseEntryDto | null;
  disabled: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onCreate: (data: CreateAiKnowledgeBaseEntryRequestDto) => void;
  onSave: (id: string, data: UpdateAiKnowledgeBaseEntryRequestDto) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation('company');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [sourceType, setSourceType] =
    useState<AiKnowledgeBaseSourceType>('manual');
  const [sourceTitle, setSourceTitle] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [reviewedAt, setReviewedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [companyPrompt, setCompanyPrompt] = useState('');
  const [forbiddenTopicsText, setForbiddenTopicsText] = useState('');
  const [handoffRulesText, setHandoffRulesText] = useState('');
  const [channelIdsText, setChannelIdsText] = useState('');
  const [crmConnectionIdsText, setCrmConnectionIdsText] = useState('');
  const [pipelineIdsText, setPipelineIdsText] = useState('');
  const [userRoleIdsText, setUserRoleIdsText] = useState('');
  const [scenariosText, setScenariosText] = useState('');
  const [aiModes, setAiModes] =
    useState<AiKnowledgeBaseAiMode[]>(getInitialAiModes(entry));

  useEffect(() => {
    setTitle(entry?.title ?? '');
    setContent(entry?.content ?? '');
    setTagsText(formatCommaList(entry?.tags ?? []));
    setEnabled(entry?.enabled ?? true);
    setSourceType(entry?.source_type ?? 'manual');
    setSourceTitle(entry?.source_title ?? '');
    setSourceUrl(entry?.source_url ?? '');
    setReviewedAt(isoToDateInput(entry?.reviewed_at ?? null));
    setExpiresAt(isoToDateInput(entry?.expires_at ?? null));
    setCompanyPrompt(entry?.company_prompt ?? '');
    setForbiddenTopicsText(formatLines(entry?.forbidden_topics ?? []));
    setHandoffRulesText(formatLines(entry?.handoff_rules ?? []));
    setChannelIdsText(formatCommaList(entry?.applicability.channel_ids ?? []));
    setCrmConnectionIdsText(
      formatCommaList(entry?.applicability.crm_connection_ids ?? [])
    );
    setPipelineIdsText(formatCommaList(entry?.applicability.pipeline_ids ?? []));
    setUserRoleIdsText(formatCommaList(entry?.applicability.user_role_ids ?? []));
    setScenariosText(formatCommaList(entry?.applicability.scenarios ?? []));
    setAiModes(getInitialAiModes(entry));
  }, [entry]);

  const formData = useMemo(() => {
    const normalizedModes = normalizeAiModes(aiModes);

    return {
      title: title.trim(),
      content: content.trim(),
      tags: parseCommaList(tagsText, 10),
      enabled,
      company_prompt: companyPrompt.trim() || null,
      forbidden_topics: parseLines(forbiddenTopicsText, 50),
      handoff_rules: parseLines(handoffRulesText, 50),
      source_type: sourceType,
      source_url: sourceUrl.trim() || null,
      source_title: sourceTitle.trim() || null,
      reviewed_at: dateInputToIso(reviewedAt),
      expires_at: dateInputToIso(expiresAt),
      applicability: {
        channel_ids: parseCommaList(channelIdsText, 50),
        crm_connection_ids: parseCommaList(crmConnectionIdsText, 50),
        pipeline_ids: parseCommaList(pipelineIdsText, 50),
        user_role_ids: parseCommaList(userRoleIdsText, 50),
        scenarios: parseCommaList(scenariosText, 50),
        ai_modes: normalizedModes,
      },
    } satisfies CreateAiKnowledgeBaseEntryRequestDto;
  }, [
    aiModes,
    channelIdsText,
    companyPrompt,
    content,
    crmConnectionIdsText,
    enabled,
    expiresAt,
    forbiddenTopicsText,
    handoffRulesText,
    pipelineIdsText,
    reviewedAt,
    scenariosText,
    sourceTitle,
    sourceType,
    sourceUrl,
    tagsText,
    title,
    userRoleIdsText,
  ]);

  const busy = isSaving || isDeleting;
  const canSubmit =
    !disabled && !busy && formData.title !== '' && formData.content !== '';
  const modeTitle = entry
    ? t('modules.knowledgeBase.editor.editTitle')
    : t('modules.knowledgeBase.editor.createTitle');

  const toggleAiMode = (mode: AiKnowledgeBaseAiMode, checked: boolean) => {
    setAiModes((current) =>
      normalizeAiModes(
        checked
          ? Array.from(new Set([...current, mode]))
          : current.filter((item) => item !== mode)
      )
    );
  };

  return (
    <Stack data-testid="knowledge-base-editor" spacing={1.5} sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {modeTitle}
          </Typography>
          {entry ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={`v${entry.version}`}
                sx={{ borderRadius: 1 }}
              />
              <Chip
                size="small"
                label={t(`modules.knowledgeBase.freshness.${entry.freshness}`)}
                sx={{ borderRadius: 1 }}
                variant="outlined"
              />
            </Stack>
          ) : null}
        </Box>
        <FormControlLabel
          sx={{
            m: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: 14,
              lineHeight: 1.35,
            },
          }}
          control={(
            <Switch
              size="small"
              checked={enabled}
              disabled={disabled || busy}
              onChange={(event) => setEnabled(event.target.checked)}
            />
          )}
          label={
            enabled
              ? t('modules.knowledgeBase.enabled')
              : t('modules.knowledgeBase.disabled')
          }
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) 180px' },
          gap: 1,
        }}
      >
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.title')}
          value={title}
          disabled={disabled || busy}
          onChange={(event) => setTitle(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          select
          size="small"
          label={t('modules.knowledgeBase.fields.sourceType')}
          value={sourceType}
          disabled={disabled || busy}
          onChange={(event) =>
            setSourceType(event.target.value as AiKnowledgeBaseSourceType)
          }
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        >
          <MenuItem value="manual">
            {t('modules.knowledgeBase.sourceTypes.manual')}
          </MenuItem>
          <MenuItem value="document">
            {t('modules.knowledgeBase.sourceTypes.document')}
          </MenuItem>
          <MenuItem value="url">
            {t('modules.knowledgeBase.sourceTypes.url')}
          </MenuItem>
        </TextField>
      </Box>

      <TextField
        size="small"
        multiline
        minRows={8}
        label={t('modules.knowledgeBase.fields.content')}
        value={content}
        disabled={disabled || busy}
        onChange={(event) => setContent(event.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1,
        }}
      >
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.tags')}
          value={tagsText}
          disabled={disabled || busy}
          helperText={t('modules.knowledgeBase.tagsHint')}
          onChange={(event) => setTagsText(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.sourceTitle')}
          value={sourceTitle}
          disabled={disabled || busy}
          onChange={(event) => setSourceTitle(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          size="small"
          label={t('modules.knowledgeBase.fields.sourceUrl')}
          value={sourceUrl}
          disabled={disabled || busy}
          onChange={(event) => setSourceUrl(event.target.value)}
          sx={{
            gridColumn: { xs: 'auto', md: '1 / -1' },
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1,
        }}
      >
        <TextField
          size="small"
          type="date"
          label={t('modules.knowledgeBase.fields.reviewedAt')}
          value={reviewedAt}
          disabled={disabled || busy}
          onChange={(event) => setReviewedAt(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          size="small"
          type="date"
          label={t('modules.knowledgeBase.fields.expiresAt')}
          value={expiresAt}
          disabled={disabled || busy}
          onChange={(event) => setExpiresAt(event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <TextField
        size="small"
        multiline
        minRows={2}
        label={t('modules.knowledgeBase.fields.companyPrompt')}
        value={companyPrompt}
        disabled={disabled || busy}
        onChange={(event) => setCompanyPrompt(event.target.value)}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 1,
          },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1,
        }}
      >
        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('modules.knowledgeBase.fields.forbiddenTopics')}
          value={forbiddenTopicsText}
          disabled={disabled || busy}
          helperText={t('modules.knowledgeBase.onePerLineHint')}
          onChange={(event) => setForbiddenTopicsText(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
        <TextField
          size="small"
          multiline
          minRows={3}
          label={t('modules.knowledgeBase.fields.handoffRules')}
          value={handoffRulesText}
          disabled={disabled || busy}
          helperText={t('modules.knowledgeBase.onePerLineHint')}
          onChange={(event) => setHandoffRulesText(event.target.value)}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={800}>
          {t('modules.knowledgeBase.applicability.title')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1,
          }}
        >
          <TextField
            size="small"
            label={t('modules.knowledgeBase.applicability.channelIds')}
            value={channelIdsText}
            disabled={disabled || busy}
            onChange={(event) => setChannelIdsText(event.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
          <TextField
            size="small"
            label={t('modules.knowledgeBase.applicability.crmConnectionIds')}
            value={crmConnectionIdsText}
            disabled={disabled || busy}
            onChange={(event) => setCrmConnectionIdsText(event.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
          <TextField
            size="small"
            label={t('modules.knowledgeBase.applicability.pipelineIds')}
            value={pipelineIdsText}
            disabled={disabled || busy}
            onChange={(event) => setPipelineIdsText(event.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
          <TextField
            size="small"
            label={t('modules.knowledgeBase.applicability.userRoleIds')}
            value={userRoleIdsText}
            disabled={disabled || busy}
            onChange={(event) => setUserRoleIdsText(event.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
          <TextField
            size="small"
            label={t('modules.knowledgeBase.applicability.scenarios')}
            value={scenariosText}
            disabled={disabled || busy}
            onChange={(event) => setScenariosText(event.target.value)}
            sx={{
              gridColumn: { xs: 'auto', md: '1 / -1' },
              '& .MuiInputBase-root': {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        <FormGroup row>
          {AI_MODES.map((mode) => (
            <FormControlLabel
              key={mode}
              control={(
                <Checkbox
                  size="small"
                  checked={aiModes.includes(mode)}
                  disabled={disabled || busy}
                  onChange={(event) => toggleAiMode(mode, event.target.checked)}
                />
              )}
              label={t(`modules.knowledgeBase.aiModes.${mode}`)}
            />
          ))}
        </FormGroup>
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        justifyContent="space-between"
      >
        {entry ? (
          <Button
            variant="outlined"
            color="error"
            disabled={disabled || busy}
            onClick={() => {
              if (window.confirm(t('modules.knowledgeBase.deleteConfirm'))) {
                onDelete(entry.id);
              }
            }}
            sx={{
              minHeight: 36,
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            {t('modules.knowledgeBase.delete')}
          </Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            if (entry) {
              onSave(entry.id, formData);
              return;
            }

            onCreate(formData);
          }}
          sx={{
            minHeight: 36,
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          {entry
            ? t('modules.knowledgeBase.save')
            : t('modules.knowledgeBase.add')}
        </Button>
      </Stack>
    </Stack>
  );
}
